import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import AvailabilityStatus from '@bibliocommons/availability-status';
import { availabilityShape, bibShape } from '@bibliocommons/bc-prop-types';
import Capsule from '@bibliocommons/capsule';
import { Dropdown, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import { Button } from '@bibliocommons/base-buttons';
import { ChevronDown, ChevronRight } from '@bibliocommons/base-icons';
import { bibFormatChooser } from '@bibliocommons/analytics-event-composers';
import { sendAnalyticsEvents } from '@bibliocommons/utils-analytics';

import BibLink from 'app/components/shared/BibLink';
import BibManifestationInfo from '../BibManifestationInfo';

import './FormatChooserCapsule.scss';

const { composeFormatPickerSelectionEvent } = bibFormatChooser;

export function FormatChooserCapsule(props) {
  function renderEdition(edition) {
    const { bib } = props;
    const handleClick = () => {
      sendAnalyticsEvents(composeFormatPickerSelectionEvent(`${bib.getIn(['briefInfo', 'format'])} | other-edition`));
    };
    return (
      <DropdownMenuItem key={edition.get('id')} handleClick={handleClick}>
        <BibLink metadataId={edition.getIn(['briefInfo', 'metadataId'])} dataKey="edition-link">
          <BibManifestationInfo
            bib={edition}
            availability={edition.get('availability')}
            showAvailability
            showPublicationDate
            showEdition
          />
        </BibLink>
      </DropdownMenuItem>
    );
  }

  function renderAdditionalInfo() {
    const { isActive, editions } = props;
    if (isActive)
      return editions && editions.size > 0 ? (
        <Dropdown
          renderTrigger={handleToggle => (
            <Button handleClick={handleToggle} type="button" dataKey="expand-editions" className="expand-editions">
              <FormattedMessage tagName="span" id="additional_edition_count" values={{ count: editions.size }} />
              <ChevronDown />
            </Button>
          )}
          dataKey="editions-dropdown"
        >
          {props.editions.map(renderEdition)}
        </Dropdown>
      ) : null;
    return (
      <AvailabilityStatus
        aria-hidden
        rollUpStatus={props.availability.get('statusType')}
        status={props.availability.get('localisedStatus')}
        availabilityLocationType={props.availability.get('availabilityLocationType')}
      />
    );
  }

  const { isActive, availability, bib, className, displayChevron, formatsCount, ...rest } = props;
  const statusType = availability.get('statusType').toLowerCase();

  const contentClassNames = cn('format-chooser-capsule-content', {
    [statusType]: isActive
  });

  const capsuleProps = {
    active: isActive,
    className: cn('cp-format-chooser-capsule', className),
    contentClassName: contentClassNames,
    dataKey: 'format-chooser-capsule',
    renderSecondaryInfo: renderAdditionalInfo,
    ...rest
  };

  return (
    <Capsule {...capsuleProps}>
      <BibManifestationInfo
        bib={bib}
        availability={availability}
        showPublicationDate={isActive}
        formatsCount={formatsCount}
      />
      {displayChevron && <ChevronRight />}
    </Capsule>
  );
}

FormatChooserCapsule.defaultProps = {
  displayChevron: true
};

FormatChooserCapsule.propTypes = {
  bib: bibShape.isRequired,
  availability: availabilityShape.isRequired,
  editions: ImmutablePropTypes.listOf(bibShape),
  className: PropTypes.string,
  handleClick: PropTypes.func,
  isActive: PropTypes.bool,
  href: PropTypes.string,
  displayChevron: PropTypes.bool,
  formatsCount: PropTypes.number
};

export default FormatChooserCapsule;
